import type { Teams } from "@/lib/arbitrage/labels";
import type { BetLinkSet } from "@/lib/bet-links";
import type { LineHistorySeries } from "@/components/odds/line-sparkline";
import type { OpenerMarket } from "@/components/odds/multibook-compare";

export type OddsHistoryFairContext =
  | { display: "odds"; american: number; decimal?: number }
  | { display: "odds"; american?: never; decimal: number }
  | { display: "probability"; probability: number };

export type OddsHistoryValueContext =
  | { kind: "roi"; percent: number }
  | { kind: "probability_edge"; percent: number };

export type OddsHistoryQuoteCapacity =
  | { kind: "limit"; amount: number }
  | { kind: "liquidity"; amount: number };

export interface OddsHistoryQuoteContext {
  oddsAmerican?: number;
  betLinks?: BetLinkSet;
  capacity?: OddsHistoryQuoteCapacity;
  isBestPrice?: true;
}

export interface OddsHistorySelectionContext {
  fair?: OddsHistoryFairContext;
  value?: OddsHistoryValueContext;
  quote?: OddsHistoryQuoteContext;
}

export interface OddsHistoryOpposite {
  selectionId?: string;
  side: string;
  line: number | null;
  context?: OddsHistorySelectionContext;
  peers?: OddsHistoryPeerBook[];
}

export interface OddsHistorySelection {
  eventId: string;
  book: string;
  selectionId?: string;
  side: string;
  line: number | null;
  mainLine?: number | null;
  market: string;
  marketLabel?: string;
  openerMarket?: OpenerMarket;
  entity?: string | null;
  eventStart: Date | string | null;
  matchup: string;
  teams?: Teams;
  opposite?: OddsHistoryOpposite;
  context?: OddsHistorySelectionContext;
}

export interface OddsHistoryPeerBook {
  book: string;
  bookDisplayName: string;
  selectionId?: string;
  side?: string;
  line: number | null;
  oddsAmerican: number;
  betLinks?: BetLinkSet;
  capacity?: OddsHistoryQuoteCapacity;
  isBestPrice?: true;
}

export interface OddsHistoryModalParams {
  selection: OddsHistorySelection;
  fallbackSeries: LineHistorySeries;
  peers?: OddsHistoryPeerBook[];
  initialView?: "book" | "compare";
}

export type OpenOddsHistory = (params: OddsHistoryModalParams) => void;

export function canAnchorOddsHistory(selection: OddsHistorySelection): boolean {
  return Boolean(
    selection.eventId &&
      selection.book &&
      selection.side &&
      selection.selectionId,
  );
}

export function getOddsHistorySelectionIdentity(
  selection: OddsHistorySelection,
): string {
  return [
    selection.eventId,
    selection.book,
    selection.selectionId ?? "",
    selection.side,
    selection.line == null ? "" : String(selection.line),
    selection.market,
    selection.entity ?? "",
  ].join("\u0000");
}

export function teamsForSide(
  side: string,
  team: string | null | undefined,
  opponent: string | null | undefined,
  teamShort?: string | null,
  opponentShort?: string | null,
): Teams | undefined {
  if (!team || !opponent) {
    return undefined;
  }
  const own = side.toLowerCase();
  if (own === "home") {
    return { home: team, away: opponent, homeShort: teamShort ?? null, awayShort: opponentShort ?? null };
  }
  if (own === "away") {
    return { home: opponent, away: team, homeShort: opponentShort ?? null, awayShort: teamShort ?? null };
  }
  return undefined;
}
